import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Palette, Ruler, Sparkles, ShieldCheck, Quote } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "../components/ui/sonner";

const HERO = "https://images.unsplash.com/photo-1636144896336-b056be4a8dfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwbWF0dXJlJTIwYmxhY2slMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODYyODc4Mnww&ixlib=rb-4.1.0&q=85";
const IMG_STYLE = "https://images.unsplash.com/photo-1747848214256-34a8cd21abac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxzdHlsaXNoJTIwb2xkZXIlMjBibGFjayUyMHdvbWFuJTIwZmFzaGlvbnxlbnwwfHx8fDE3ODg2Mjg3ODJ8MA&ixlib=rb-4.1.0&q=85";
const IMG_OCC = "https://images.unsplash.com/photo-1763766276860-0d691bac577f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxzdHlsaXNoJTIwb2xkZXIlMjBibGFjayUyMHdvbWFuJTIwZmFzaGlvbnxlbnwwfHx8fDE3ODg2Mjg3ODJ8MA&ixlib=rb-4.1.0&q=85";
const IMG_COLOR = "https://customer-assets-lxgj4vgw.emergentagent.net/job_color-confidence-2/artifacts/milmybva_Style%20%282%29.png";

const FEATURES = [
  { icon: Ruler, title: "Fit before size", body: "Your real proportions guide every recommendation — never a generic size chart." },
  { icon: Palette, title: "Colors made for you", body: "A personalized palette tuned to your skin's depth and undertone. We never ban a color — we teach you how to wear it." },
  { icon: Sparkles, title: "Always the why", body: "Every suggestion comes with the reasoning behind it, so you dress with intention and confidence." },
  { icon: ShieldCheck, title: "Yours, privately", body: "Your measurements are encrypted, photo analysis is opt-in, and you can delete everything in one click." },
];

export default function Landing() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", { email });
      setSent(true);
      toast.success("You're on the early-access list. Welcome.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand text-ink">
      {/* Nav */}
      <header className="flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6">
        <div className="font-serif text-2xl tracking-tight" data-testid="brand-logo">Élanora</div>
        <nav className="flex items-center gap-6">
          <Link to="/login" className="text-sm hover:text-emerald transition-colors duration-300" data-testid="nav-login">
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="nav-get-started"
            className="rounded-full bg-emerald text-white text-sm px-6 py-3 hover:bg-emerald/90 transition-colors duration-300"
          >
            Get started
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center px-6 sm:px-10 lg:px-16 pt-10 lg:pt-16 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <p className="eyebrow mb-6">The personal styling studio</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Style designed<br />around <em className="not-italic text-emerald">you</em>,<br />
            not a size chart.
          </h1>
          <p className="mt-8 text-lg sm:text-xl leading-relaxed text-ink/70 max-w-xl">
            A personal AI stylist for Black women 40 and beyond. Garments, colors and complete
            looks built from your real measurements, your skin tone and your life — always with
            the reasoning behind every choice.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/register"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-emerald text-white text-base px-8 py-4 hover:bg-emerald/90 transition-colors duration-300"
            >
              Build my style profile
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
            </Link>
            <a href="#early" className="text-base underline underline-offset-4 decoration-1 hover:text-emerald transition-colors duration-300">
              Join early access
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="overflow-hidden rounded-[2px] border border-ink/10">
            <img src={HERO} alt="Elegant woman in a tailored blazer" className="w-full h-[520px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-4 sm:left-6 bg-sand border border-ink/10 px-6 py-5 max-w-xs">
            <p className="font-serif text-xl leading-snug">"Confidence over correction."</p>
            <p className="text-sm text-ink/60 mt-2">One of our seven styling principles</p>
          </div>
        </motion.div>
      </section>

      {/* Principles / features */}
      <section className="px-6 sm:px-10 lg:px-16 py-20 bg-[#F2EFEB]">
        <p className="eyebrow mb-4">Why Élanora is different</p>
        <h2 className="text-4xl sm:text-5xl tracking-tight max-w-2xl">Styling that begins with you, in every sense.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-sand border border-ink/10 p-8"
              data-testid={`feature-${i}`}
            >
              <f.icon className="w-7 h-7 text-emerald" strokeWidth={1.5} />
              <h3 className="text-2xl mt-6">{f.title}</h3>
              <p className="text-base text-ink/70 mt-3 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Editorial split */}
      <section className="grid lg:grid-cols-2">
        <div className="relative overflow-hidden aspect-[4/5] lg:aspect-auto lg:min-h-[720px]">
          <img src={IMG_COLOR} alt="Woman with rich color styling" className="absolute inset-0 w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700 ease-out" />
        </div>
        <div className="bg-emerald text-white px-8 sm:px-14 py-20 flex flex-col justify-center">
          <p className="eyebrow mb-5 text-gold">Your color story</p>
          <h2 className="text-4xl sm:text-5xl tracking-tight text-white">A palette optimised for deep, rich skin.</h2>
          <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-lg">
            Most color systems were never built with you in mind. Ours is. Choose your depth and
            undertone and receive a five-tier palette — from your exceptional power colors to shades
            that simply need thoughtful styling. Nothing is off-limits.
          </p>
          <div className="flex gap-3 mt-10">
            {["#04471C", "#660018", "#0A3B85", "#C19A5B", "#B4267A", "#12A19A"].map((c) => (
              <span key={c} className="w-12 h-12 rounded-full border border-white/30" style={{ background: c }} />
            ))}
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section className="grid lg:grid-cols-2 items-center gap-12 px-6 sm:px-10 lg:px-16 py-24">
        <div>
          <p className="eyebrow mb-5">For every room you walk into</p>
          <h2 className="text-4xl sm:text-5xl tracking-tight">Executive. Faith leader. Socialite. All of you.</h2>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed max-w-xl">
            Whether you're chairing a board, leading a congregation, celebrating at a gala or running
            weekend errands, Élanora learns the occasions that fill your calendar and dresses you for
            each with intention.
          </p>
          <div className="flex items-start gap-4 mt-10 border-l-2 border-emerald pl-6">
            <Quote className="w-6 h-6 text-emerald shrink-0" strokeWidth={1.5} />
            <p className="font-serif text-2xl leading-snug">Age-aware, never age-restrictive.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img src={IMG_STYLE} alt="Stylish woman with silver hair" className="w-full h-72 object-cover rounded-[2px] border border-ink/10 mt-8" />
          <img src={IMG_OCC} alt="Woman in a trench coat" className="w-full h-72 object-cover object-top rounded-[2px] border border-ink/10" />
        </div>
      </section>

      {/* Early access */}
      <section id="early" className="px-6 sm:px-10 lg:px-16 py-24 bg-[#F2EFEB]">
        <div className="max-w-2xl">
          <p className="eyebrow mb-5">Early access</p>
          <h2 className="text-4xl sm:text-5xl tracking-tight">Be among the first to be styled.</h2>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed">
            The studio is coming soon. Join our founding waitlist and be among the first to get access when we open. Add your email below, and we'll keep you in the loop.
          </p>
          {sent ? (
            <p className="mt-10 font-serif text-2xl text-emerald" data-testid="waitlist-success">
              You're on the list — we can't wait to style you.
            </p>
          ) : (
            <form onSubmit={join} className="mt-10 flex flex-col sm:flex-row gap-4 max-w-lg">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="waitlist-email-input"
                className="flex-1 bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg placeholder:text-ink/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit"
                className="rounded-full bg-emerald text-white px-8 py-3 text-base hover:bg-emerald/90 transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Adding…" : "Join early access"}
              </button>
            </form>
          )}
        </div>
      </section>

      <footer className="px-6 sm:px-10 lg:px-16 py-12 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-ink/10">
        <div className="font-serif text-xl">Élanora</div>
        <p className="text-sm text-ink/50">Style designed around you. © {new Date().getFullYear()} Élanora.</p>
      </footer>
    </div>
  );
}
